import React from 'react';
import { Todo, TodoNavigationSummary, TodoTagEntity } from '../types/data';
import TodoMenuCalendar from './TodoMenuCalendar';
import TodoMenuTagList from './TodoMenuTagList';
import TodoMenuHabitList from './TodoMenuHabitList';
import InputText from './InputText';
import Icon from './icons/Icon';
import './TodoMenu.css';
import { getShoppingHabitId } from '../utils/habits';

type ViewType = 'calendar' | 'tags' | 'habits';

interface TodoMenuProps {
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  todos: Todo[];
  currentMonth?: Date;
  onCurrentMonthChange?: (date: Date) => void;
  onTagSelect?: (tagId: string | null) => void;
  selectedTagId?: string | null;
  onReorderTags?: (sourceId: string, targetId: string, insertAfter: boolean) => void | Promise<void>;
  onHabitSelect?: (habitId: string) => void;
  selectedHabitId?: string | null;
  viewMode?: 'date' | 'tag' | 'habit';
  tags: TodoTagEntity[];
  navigationSummary?: TodoNavigationSummary | null;
}

const TodoMenu: React.FC<TodoMenuProps> = ({
  selectedDate,
  onDateSelect,
  todos,
  currentMonth,
  onCurrentMonthChange,
  onTagSelect,
  selectedTagId,
  onReorderTags,
  onHabitSelect,
  selectedHabitId,
  viewMode = 'date',
  tags,
  navigationSummary
}) => {
  // viewModeをViewTypeに変換
  const getActiveViewFromViewMode = (mode: 'date' | 'tag' | 'habit'): ViewType => {
    if (mode === 'date') return 'calendar';
    if (mode === 'tag') return 'tags';
    if (mode === 'habit') return 'habits';
    return 'calendar';
  };

  const activeView = getActiveViewFromViewMode(viewMode);

  // タグ検索状態（IMEを考慮）
  const [tagSearchInput, setTagSearchInput] = React.useState('');
  const [tagFilter, setTagFilter] = React.useState('');
  const [isTagComposing, setIsTagComposing] = React.useState(false);

  // 習慣検索状態（IMEを考慮）
  const [habitSearchInput, setHabitSearchInput] = React.useState('');
  const [habitFilter, setHabitFilter] = React.useState('');
  const [isHabitComposing, setIsHabitComposing] = React.useState(false);

  const handleTabChange = (view: ViewType) => {
    // タブ切り替え時にデフォルトの表示先にナビゲート
    if (view === 'calendar') {
      // カレンダー → 今日
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];
      onDateSelect(todayString);
    } else if (view === 'tags') {
      // タグ → 未分類
      onTagSelect?.(null);
    } else if (view === 'habits') {
      // 習慣 → 買い物
      const shoppingId = getShoppingHabitId();
      if (shoppingId) onHabitSelect?.(shoppingId);
    }
  };

  return (
    <div className="todo-menu">
      {/* タブナビゲーション */}
      <div className="todo-menu-tabs">
        <button
          className={`todo-menu-tab ${activeView === 'calendar' ? 'active' : ''}`}
          onClick={() => handleTabChange('calendar')}
          aria-label="カレンダー"
        >
          <Icon name="calendar" size={24} />
        </button>
        <button
          className={`todo-menu-tab ${activeView === 'tags' ? 'active' : ''}`}
          onClick={() => handleTabChange('tags')}
          aria-label="タグ"
        >
          <Icon name="tag" size={24} />
        </button>
        <button
          className={`todo-menu-tab ${activeView === 'habits' ? 'active' : ''}`}
          onClick={() => handleTabChange('habits')}
          aria-label="習慣"
        >
          <Icon name="habit" size={24} />
        </button>
      </div>

      {/* ビュー表示エリア */}
      <div className="todo-menu-content">
        {activeView === 'calendar' && (
          <TodoMenuCalendar
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            todos={todos}
            navigationSummary={navigationSummary}
            currentMonth={currentMonth}
            onCurrentMonthChange={onCurrentMonthChange}
          />
        )}
        {activeView === 'tags' && (
          <div className="todo-tag-content">
            <InputText
              hasLeftIcon={true}
              leftIcon="search"
              placeholder="タグを検索"
              value={tagSearchInput}
              onChange={(e) => {
                const v = e.target.value;
                setTagSearchInput(v);
                if (!isTagComposing) {
                  setTagFilter(v);
                }
              }}
              onCompositionStart={() => setIsTagComposing(true)}
              onCompositionEnd={() => {
                setIsTagComposing(false);
                setTagFilter(tagSearchInput);
              }}
            />
            <TodoMenuTagList
              tags={tags}
              todos={todos}
              tagCounts={navigationSummary?.tag_counts}
              onTagSelect={onTagSelect}
              selectedTagId={selectedTagId}
              filterText={tagFilter}
              onReorderTags={onReorderTags}
            />
          </div>
        )}
        {activeView === 'habits' && (
          <div className="todo-habit-content">
            <InputText
              hasLeftIcon={true}
              leftIcon="search"
              placeholder="習慣を検索"
              value={habitSearchInput}
              onChange={(e) => {
                const v = e.target.value;
                setHabitSearchInput(v);
                if (!isHabitComposing) {
                  setHabitFilter(v);
                }
              }}
              onCompositionStart={() => setIsHabitComposing(true)}
              onCompositionEnd={() => {
                setIsHabitComposing(false);
                setHabitFilter(habitSearchInput);
              }}
            />
            <TodoMenuHabitList
              todos={todos}
              habitCounts={navigationSummary?.habit_counts}
              onHabitSelect={onHabitSelect}
              selectedHabitId={selectedHabitId}
              filterText={habitFilter}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TodoMenu;
